import { useState, useMemo } from "react";

const allCities = [
  { name: "Battle Mountain", route: "/cities/battle-mountain", tagline: "Lander County passport services", highlights: ["Lander County area coverage", "Serving Elko County corridor"], badge: null, status: "live" },
  { name: "Boulder City", route: "/cities/boulder-city", tagline: "Boulder City expedited passport services", highlights: ["Boulder City area coverage", "Serving Lake Mead, Henderson south area"], badge: null, status: "live" },
  { name: "Carson City", route: "/cities/carson-city", tagline: "State capital expedited passport services", highlights: ["Carson City metro coverage", "Serving Douglas County, Dayton, Minden"], badge: "Popular", status: "live" },
  { name: "Elko", route: "/cities/elko", tagline: "Northeastern Nevada passport services", highlights: ["Elko County area coverage", "Serving Spring Creek, Carlin, Wells"], badge: "Popular", status: "live" },
  { name: "Fallon", route: "/cities/fallon", tagline: "Churchill County passport services", highlights: ["Fallon area coverage", "Serving Fernley, Lovelock, Yerington"], badge: null, status: "live" },
  { name: "Fernley", route: "/cities/fernley", tagline: "Lyon County passport services", highlights: ["Fernley area coverage", "Serving Silver Springs, Dayton, Yerington"], badge: null, status: "live" },
  { name: "Gardnerville", route: "/cities/gardnerville", tagline: "Douglas County passport services", highlights: ["Carson Valley area coverage", "Serving Minden, Genoa, Smith Valley"], badge: null, status: "live" },
  { name: "Henderson", route: "/cities/henderson-nv", tagline: "Clark County southern metro passport hub", highlights: ["Green Valley, Anthem, Lake Las Vegas", "Serving Boulder City, Cadence, Whitney"], badge: "Popular", status: "live" },
  { name: "Incline Village", route: "/cities/incline-village", tagline: "Lake Tahoe North Shore passport services", highlights: ["Incline Village area coverage", "Serving Crystal Bay, Kings Beach area"], badge: null, status: "live" },
  { name: "Las Vegas", route: "/cities/las-vegas", tagline: "Clark County's premier expedited passport hub", highlights: ["Las Vegas metro coverage", "Serving Summerlin, Henderson, North LV, Paradise"], badge: "Most Popular", status: "live" },
  { name: "Laughlin", route: "/cities/laughlin", tagline: "Southern Clark County passport services", highlights: ["Laughlin area coverage", "Serving Bullhead City AZ, Fort Mohave"], badge: null, status: "live" },
  { name: "Mesquite", route: "/cities/mesquite-nv", tagline: "Northeast Clark County passport services", highlights: ["Mesquite area coverage", "Serving St. George UT area, Virgin Valley"], badge: null, status: "live" },
  { name: "Minden", route: "/cities/minden", tagline: "Douglas County passport services", highlights: ["Carson Valley area coverage", "Serving Gardnerville, Genoa, Smith Valley"], badge: null, status: "live" },
  { name: "North Las Vegas", route: "/cities/north-las-vegas", tagline: "Clark County northern metro passport services", highlights: ["North Las Vegas area coverage", "Serving Aliante, Cheyenne Corridor"], badge: null, status: "live" },
  { name: "Pahrump", route: "/cities/pahrump", tagline: "Nye County passport services", highlights: ["Pahrump area coverage", "Serving Beatty, Amargosa Valley, Indian Springs"], badge: null, status: "live" },
  { name: "Reno", route: "/cities/reno", tagline: "Washoe County northern Nevada passport hub", highlights: ["Reno-Sparks metro coverage", "Serving Carson City, Fernley, Lake Tahoe Basin"], badge: "Popular", status: "live" },
  { name: "Sparks", route: "/cities/sparks", tagline: "Eastern Washoe County passport hub", highlights: ["Spanish Springs, Sun Valley coverage", "Serving Tesla corridor, Eastern Nevada tech"], badge: null, status: "live" },
  { name: "Spring Creek", route: "/cities/spring-creek", tagline: "Elko County suburb passport services", highlights: ["Spring Creek area coverage", "Serving Elko, Carlin, Ruby Valley"], badge: null, status: "live" },
  { name: "Summerlin", route: "/cities/summerlin", tagline: "Western Las Vegas master-plan passport services", highlights: ["Summerlin area coverage", "Serving Red Rock Canyon area, Skye Canyon"], badge: null, status: "live" },
  { name: "Sun Valley", route: "/cities/sun-valley", tagline: "North Reno corridor passport services", highlights: ["Sun Valley area coverage", "Serving Reno north, Cold Springs, North Valleys"], badge: null, status: "live" },
  { name: "Winnemucca", route: "/cities/winnemucca", tagline: "Humboldt County passport services", highlights: ["Winnemucca area coverage", "Serving Lovelock, Battle Mountain corridor"], badge: null, status: "live" },
];

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function NevadaCitiesDirectory() {
  const [activeLetter, setActiveLetter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const lettersWithCities = useMemo(() => {
    const set = new Set<string>();
    allCities.forEach((c) => set.add(c.name[0].toUpperCase()));
    return set;
  }, []);

  const filteredCities = useMemo(() => {
    let result = allCities;
    if (activeLetter) {
      result = result.filter((c) => c.name[0].toUpperCase() === activeLetter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.tagline.toLowerCase().includes(q) ||
          c.highlights.some((h) => h.toLowerCase().includes(q))
      );
    }
    return result;
  }, [activeLetter, searchQuery]);

  const liveCount = allCities.filter((c) => c.status === "live").length;
  const comingCount = allCities.filter((c) => c.status === "coming").length;

  return (
    <section id="cities" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Nevada Cities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Choose Your Nevada Location
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed mb-2">
            Select your city to view local acceptance facilities, processing options, and expedited passport services tailored to your area of Nevada.
          </p>
          <p className="text-gray-400 text-sm">
            <span className="text-[#1A365D] font-semibold">{liveCount}</span> cities live
            &nbsp;&middot;&nbsp;
            <span className="text-[#1A365D] font-semibold">{comingCount}</span> more coming soon
          </p>
        </div>

        {/* Search */}
        <div className="max-w-xl mx-auto mb-6">
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
              <i className="ri-search-line text-gray-400"></i>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (e.target.value.trim() && activeLetter) setActiveLetter(null);
              }}
              placeholder="Search Nevada cities..."
              className="w-full pl-11 pr-10 py-3 bg-white border border-gray-200 rounded-lg text-sm text-[#1A365D] placeholder:text-gray-400 focus:outline-none focus:border-[#F6C344] focus:ring-1 focus:ring-[#F6C344] transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line"></i>
              </button>
            )}
          </div>
        </div>

        {/* Alphabet bar */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="flex flex-wrap items-center justify-center gap-1.5 md:gap-2">
            <button
              onClick={() => setActiveLetter(null)}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-all whitespace-nowrap ${
                activeLetter === null
                  ? "bg-[#1A365D] text-white"
                  : "bg-white text-gray-500 hover:bg-[#EEF2F8] hover:text-[#1A365D] border border-gray-200"
              }`}
            >
              All
            </button>
            {LETTERS.map((letter) => {
              const hasCities = lettersWithCities.has(letter);
              const isActive = activeLetter === letter;
              return (
                <button
                  key={letter}
                  onClick={() => hasCities && setActiveLetter(isActive ? null : letter)}
                  disabled={!hasCities}
                  className={`w-8 h-8 md:w-9 md:h-9 rounded-md text-sm font-semibold flex items-center justify-center transition-all ${
                    isActive
                      ? "bg-[#1A365D] text-white"
                      : hasCities
                        ? "bg-white text-[#1A365D] hover:bg-[#EEF2F8] border border-gray-200 cursor-pointer"
                        : "bg-gray-100 text-gray-300 border border-gray-100 cursor-not-allowed"
                  }`}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        </div>

        {/* Results count */}
        {(activeLetter || searchQuery) && (
          <div className="flex items-center justify-between max-w-7xl mb-6">
            <p className="text-sm text-gray-500">
              Showing <span className="font-semibold text-[#1A365D]">{filteredCities.length}</span>{" "}
              {filteredCities.length === 1 ? "city" : "cities"}
              {activeLetter && <> starting with <span className="font-semibold text-[#1A365D]">{activeLetter}</span></>}
              {searchQuery && <> matching <span className="font-semibold text-[#1A365D]">"{searchQuery}"</span></>}
            </p>
            <button
              onClick={() => { setActiveLetter(null); setSearchQuery(""); }}
              className="text-sm text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors whitespace-nowrap"
            >
              Clear filters
            </button>
          </div>
        )}

        {/* City grid */}
        {filteredCities.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCities.map((city, i) =>
              city.status === "live" ? (
                <a
                  key={i}
                  href={city.route}
                  className="group relative bg-white border border-gray-200 rounded-lg p-7 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
                >
                  {city.badge && (
                    <span className="absolute top-4 right-4 bg-[#1A365D] text-white text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
                      {city.badge}
                    </span>
                  )}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0">
                      <i className="ri-map-pin-2-line text-[#1A365D] text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1A365D] text-lg">{city.name}</h3>
                      <p className="text-gray-400 text-xs">Nevada</p>
                    </div>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{city.tagline}</p>
                  <ul className="space-y-2 mb-6 flex-1">
                    {city.highlights.map((h, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-check-line text-[#F6C344] flex-shrink-0"></i>
                        {h}
                      </li>
                    ))}
                  </ul>
                  <div className="inline-flex items-center gap-1.5 text-[#1A365D] font-semibold text-sm group-hover:text-[#F6C344] transition-colors mt-auto whitespace-nowrap">
                    View Services <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i>
                  </div>
                </a>
              ) : (
                <div key={i} className="group relative bg-white border border-dashed border-gray-300 rounded-lg p-7 flex flex-col">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 flex-shrink-0">
                      <i className="ri-time-line text-gray-400 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-400 text-lg">{city.name}</h3>
                      <span className="inline-block bg-gray-100 text-gray-500 text-xs font-semibold px-2 py-0.5 rounded-full mt-0.5">Coming Soon</span>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm leading-relaxed mb-4">{city.tagline}</p>
                  <ul className="space-y-2 mb-6 flex-1">
                    {city.highlights.map((h, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-gray-400">
                        <i className="ri-check-line text-gray-300 flex-shrink-0"></i>
                        {h}
                      </li>
                    ))}
                  </ul>
                  <div className="inline-flex items-center gap-1.5 text-gray-400 font-semibold text-sm mt-auto whitespace-nowrap">
                    Coming Soon <i className="ri-time-line"></i>
                  </div>
                </div>
              )
            )}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#EEF2F8] mx-auto mb-4">
              <i className="ri-search-line text-[#1A365D] text-2xl"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-lg mb-2">No cities found</h3>
            <p className="text-gray-500 text-sm max-w-md mx-auto">Try a different search term or letter. We&apos;re always adding new Nevada locations.</p>
            <button
              onClick={() => { setActiveLetter(null); setSearchQuery(""); }}
              className="mt-4 inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-sm transition-colors whitespace-nowrap"
            >
              Show all cities <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}